import React, { useEffect, useState } from "react";
import {
  X,
  Target,
  DollarSign,
  Search,
  Zap,
  Plus,
  Info,
  Link as LinkIcon,
  MousePointer2,
} from "lucide-react";
import { useTranslation } from "../../contexts/LanguageContext";

interface Sitelink {
  title: string;
  description1: string;
  description2: string;
  finalUrl: string;
}

interface BoostGoogleModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: any) => Promise<void>;
  loading: boolean;
  post: any;
}

const BoostGoogleModal: React.FC<BoostGoogleModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading,
}) => {
  const { t } = useTranslation();
  const [dailyBudget, setDailyBudget] = useState(30);
  const [duration, setDuration] = useState(7);
  const [campaignType, setCampaignType] = useState<
    "search" | "performance_max"
  >("search");
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState<string[]>([]);
  const [sitelinks, setSitelinks] = useState<Sitelink[]>([
    { title: "", description1: "", description2: "", finalUrl: "" },
    { title: "", description1: "", description2: "", finalUrl: "" },
  ]);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const totalBudget = dailyBudget * duration;

  const addKeyword = () => {
    if (keywordInput.trim() && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
      setKeywordInput("");
    }
  };

  const removeKeyword = (kw: string) => {
    setKeywords(keywords.filter((k) => k !== kw));
  };

  const updateSitelink = (
    index: number,
    field: keyof Sitelink,
    value: string,
  ) => {
    const newSitelinks = [...sitelinks];
    newSitelinks[index] = { ...newSitelinks[index], [field]: value };
    setSitelinks(newSitelinks);
  };

  const handleSubmit = async () => {
    await onConfirm({
      daily_budget_brl: dailyBudget,
      duration_days: duration,
      campaign_type: campaignType,
      keywords,
      sitelinks: sitelinks.filter((s) => s.title.trim()),
    });
  };

  return (
    <div className="fixed inset-0 z-100 flex items-center justify-center bg-black/60 backdrop-blur-xs animate-in fade-in duration-300">
      <div className="bg-[#121214] border border-white/10 w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-300 max-h-[90vh]">
        {/* Header */}
        <div className="px-8 py-6 border-b border-white/5 flex items-center justify-between bg-linear-to-r from-blue-600/20 to-indigo-600/10">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-600 rounded-2xl shadow-xl shadow-blue-600/20">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-black text-white tracking-tight uppercase">
                {t("Turbinar Google Ads")}
              </h2>
              <p className="text-[10px] font-bold text-gray-500 uppercase tracking-widest mt-1">
                {t("Sincronização Direta com Google Ads")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 space-y-10 overflow-y-auto max-h-[65vh] custom-scrollbar bg-[#121214]">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Left: Settings */}
            <div className="space-y-8">
              <section className="space-y-4">
                <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] flex items-center gap-2">
                  <Target className="w-4 h-4 text-blue-500" />
                  {t("Tipo de Campanha")}
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <button
                    onClick={() => setCampaignType("search")}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      campaignType === "search"
                        ? "border-blue-500 bg-blue-500/10"
                        : "border-white/5 bg-white/5 hover:border-white/20"
                    }`}
                  >
                    <div className="font-black text-white text-sm">
                      {t("Pesquisa")}
                    </div>
                    <div className="text-[9px] text-gray-500 mt-1 uppercase font-bold tracking-widest">
                      Google Search
                    </div>
                  </button>
                  <button
                    onClick={() => setCampaignType("performance_max")}
                    className={`p-4 rounded-2xl border-2 text-left transition-all ${
                      campaignType === "performance_max"
                        ? "border-blue-500 bg-blue-500/10"
                        : "border-white/5 bg-white/5 hover:border-white/20"
                    }`}
                  >
                    <div className="font-black text-white text-sm">
                      Performance Max
                    </div>
                    <div className="text-[9px] text-gray-500 mt-1 uppercase font-bold tracking-widest">
                      AI Multi-Channel
                    </div>
                  </button>
                </div>
              </section>

              <section className="space-y-6 bg-white/5 p-6 rounded-[32px] border border-white/5">
                <div className="flex items-center justify-between">
                  <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-green-500" />
                    {t("Investimento")}
                  </h3>
                  <div className="bg-green-500/10 px-4 py-1.5 rounded-full border border-green-500/20">
                    <span className="text-xs font-black text-green-500">
                      {t("Total: R$ {total}", {
                        total: totalBudget.toFixed(2),
                      })}
                    </span>
                  </div>
                </div>
                <div className="space-y-8">
                  <div className="space-y-4">
                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest">
                      <span className="text-gray-500">{t("Diário")}</span>
                      <span className="text-blue-400">R$ {dailyBudget}</span>
                    </div>
                    <input
                      type="range"
                      min="10"
                      max="1000"
                      value={dailyBudget}
                      onChange={(e) => setDailyBudget(parseInt(e.target.value))}
                      className="w-full accent-blue-500 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between text-[11px] font-black uppercase tracking-widest">
                      <span className="text-gray-500">{t("Duração")}</span>
                      <span className="text-blue-400">
                        {t("{days} dias", { days: duration })}
                      </span>
                    </div>
                    <input
                      type="range"
                      min="1"
                      max="90"
                      value={duration}
                      onChange={(e) => setDuration(parseInt(e.target.value))}
                      className="w-full accent-blue-500 h-2 bg-white/10 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              </section>

              <section className="space-y-4">
                <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] flex items-center gap-2">
                  <Search className="w-4 h-4 text-blue-500" />
                  {t("Palavras-chave")}
                </h3>
                <div className="relative">
                  <Plus className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-500" />
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addKeyword()}
                    placeholder={t("Adicionar palavra-chave...")}
                    className="w-full h-[54px] pl-12 pr-12 bg-black/40 border border-white/5 rounded-2xl text-xs font-bold text-white placeholder:text-gray-600 focus:border-blue-500/50 outline-hidden transition-all"
                  />
                  <button
                    onClick={addKeyword}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-xs font-black text-blue-500 hover:text-blue-400"
                  >
                    ADD
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 pt-1">
                  {keywords.map((kw, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase"
                    >
                      {kw}
                      <button
                        onClick={() => removeKeyword(kw)}
                        className="hover:text-red-400"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </section>
            </div>

            {/* Right: Sitelinks */}
            <div className="space-y-8">
              <section className="space-y-6">
                <h3 className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] flex items-center gap-2">
                  <LinkIcon className="w-4 h-4 text-blue-500" />
                  {t("Sitelinks (Opcional)")}
                </h3>
                <div className="space-y-6">
                  {sitelinks.map((s, i) => (
                    <div
                      key={i}
                      className="p-6 bg-white/5 rounded-3xl border border-white/5 space-y-4"
                    >
                      <div className="text-[10px] font-black text-blue-500 uppercase tracking-widest bg-blue-500/10 px-3 py-1 rounded-full w-fit">
                        {t("Sitelink #{index}", { index: i + 1 })}
                      </div>
                      <div className="space-y-3">
                        <input
                          placeholder={t("Título (Máx 25)")}
                          maxLength={25}
                          value={s.title}
                          onChange={(e) =>
                            updateSitelink(i, "title", e.target.value)
                          }
                          className="w-full bg-black/40 border border-white/5 rounded-xl px-4 py-2.5 text-xs text-white placeholder:text-gray-600 focus:border-blue-500/50 outline-hidden"
                        />
                        <div className="grid grid-cols-1 gap-2">
                          <input
                            placeholder={t("Descrição 1 (Máx 35)")}
                            maxLength={35}
                            value={s.description1}
                            onChange={(e) =>
                              updateSitelink(i, "description1", e.target.value)
                            }
                            className="w-full bg-black/40 border border-white/5 rounded-xl px-4 py-2 text-[10px] text-white placeholder:text-gray-600 focus:border-blue-500/50 outline-hidden"
                          />
                          <input
                            placeholder={t("Descrição 2 (Máx 35)")}
                            maxLength={35}
                            value={s.description2}
                            onChange={(e) =>
                              updateSitelink(i, "description2", e.target.value)
                            }
                            className="w-full bg-black/40 border border-white/5 rounded-xl px-4 py-2 text-[10px] text-white placeholder:text-gray-600 focus:border-blue-500/50 outline-hidden"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                  {sitelinks.length < 4 && (
                    <button
                      onClick={() =>
                        setSitelinks([
                          ...sitelinks,
                          {
                            title: "",
                            description1: "",
                            description2: "",
                            finalUrl: "",
                          },
                        ])
                      }
                      className="w-full py-4 border-2 border-dashed border-white/10 rounded-3xl text-gray-500 text-[10px] font-black uppercase hover:border-blue-500/30 hover:text-blue-500 transition-all flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {t("Adicionar Mais Sitelinks")}
                    </button>
                  )}
                </div>
              </section>
            </div>
          </div>

          <div className="p-6 bg-blue-600/5 border border-blue-600/10 rounded-[32px] flex gap-4">
            <div className="p-3 bg-blue-600/10 rounded-2xl h-fit">
              <Info className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <h4 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-1">
                {t("Nota Estratégica")}
              </h4>
              <p className="text-[11px] text-blue-200/60 leading-relaxed font-medium">
                {t(
                  "Sitelinks aumentam seu CTR em até 20%. Recomendamos preencher todos os campos para maximizar o Índice de Qualidade do Google Ads.",
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-white/5 bg-black/40 flex items-center justify-between">
          <button
            onClick={onClose}
            className="px-6 py-2.5 text-sm font-medium text-gray-400 hover:text-white transition-colors"
          >
            {t("Cancelar")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="relative group overflow-hidden px-10 py-3 rounded-2xl bg-blue-600 text-white font-black text-xs uppercase tracking-widest shadow-2xl shadow-blue-600/30 hover:scale-[1.02] transition-all disabled:opacity-50"
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>{t("LANÇANDO...")}</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <span>{t("LANÇAR NO GOOGLE")}</span>
                <MousePointer2 className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoostGoogleModal;
